import { useState, type FormEvent } from 'react'
import { Reveal, Seo } from '@/components/primitives'

const INDUSTRIES = [
  'Insurance: Medicare / ACA / Life / P&C',
  'Contact center / BPO',
  'In-person sales',
  'Solar',
  'Other regulated, high-volume operation',
]

const SEATS = ['1-10', '11-50', '51-150', '151-500', '500+']

const TIMELINES = ['Now (< 30 days)', '30-90 days', '90+ days', 'Just exploring']

const EXPECTATIONS = [
  'A 30-minute live walkthrough on your use case.',
  'Your chain mapped end to end.',
  'Your security and compliance questions answered.',
]

export default function Walkthrough() {
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
  }

  return (
    <div className="theme-light">
      <Seo
        title="Book a walkthrough | TLDNXS"
        description="Show us your operation. We map TLDNXS on it, live. Reply within one business day."
      />

      <section aria-labelledby="walkthrough-title" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="field-grid pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(75%_65%_at_50%_15%,black,transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 md:py-24">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="text-center lg:col-span-5 lg:text-left">
              <Reveal>
                <p className="eyebrow">See it on your operation</p>
                <h1
                  id="walkthrough-title"
                  className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-left"
                >
                  Show us your operation. We&rsquo;ll map TLDNXS on it.
                </h1>
                <p className="mt-6 text-base leading-relaxed text-[#3D4854] md:text-lg">
                  Bring the sources, queues, handoffs, and stuck work. We run a live walkthrough on
                  your shape of the business, and reply within one business day.
                </p>
                <p className="mt-6 text-sm leading-relaxed text-[#3D4854]">
                  This is the fastest path from “interesting” to “this is how it runs for us.”
                </p>
                <ul className="mt-8 space-y-3">
                  {EXPECTATIONS.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-[#10161D]">
                      <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-signal-deep" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={100}>
                {submitted ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className="panel rounded-sm border p-8 md:p-10"
                  >
                    <p className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 items-center justify-center rounded-sm bg-good/15 font-mono text-base text-[#0B7A45]"
                      >
                        ✓
                      </span>
                      <span className="text-xl font-extrabold tracking-tight text-[#10161D]">
                        Transmission received.
                      </span>
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-[#3D4854]">
                      Check your inbox for confirmation. We will be in touch within one business
                      day.
                    </p>
                    <p className="mt-6 border-t border-[#C9D2DB] pt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-[#6B7684]">
                      Preview build: this form does not transmit or store data.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="panel rounded-sm border p-6 md:p-8"
                    aria-label="Book a walkthrough"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="wt-name" className="field-label">
                          Name <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <input id="wt-name" name="name" type="text" required autoComplete="name" className="field-input" />
                      </div>
                      <div>
                        <label htmlFor="wt-email" className="field-label">
                          Work email <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <input id="wt-email" name="email" type="email" required autoComplete="email" className="field-input" />
                      </div>
                      <div>
                        <label htmlFor="wt-company" className="field-label">
                          Company <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <input id="wt-company" name="company" type="text" required autoComplete="organization" className="field-input" />
                      </div>
                      <div>
                        <label htmlFor="wt-phone" className="field-label">
                          Phone <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <input id="wt-phone" name="phone" type="tel" required autoComplete="tel" className="field-input" />
                      </div>
                      <div>
                        <label htmlFor="wt-role" className="field-label">Role</label>
                        <input id="wt-role" name="role" type="text" autoComplete="organization-title" className="field-input" placeholder="Operations director, owner, admin…" />
                      </div>
                      <div>
                        <label htmlFor="wt-industry" className="field-label">
                          Industry <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <select id="wt-industry" name="industry" required defaultValue="" className="field-input">
                          <option value="" disabled>Select…</option>
                          {INDUSTRIES.map((i) => (
                            <option key={i} value={i}>{i}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="wt-seats" className="field-label">
                          Agent seats <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <select id="wt-seats" name="seats" required defaultValue="" className="field-input">
                          <option value="" disabled>Select…</option>
                          {SEATS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="wt-timeline" className="field-label">
                          Timeline <span aria-hidden="true" className="text-block">*</span>
                        </label>
                        <select id="wt-timeline" name="timeline" required defaultValue="" className="field-input">
                          <option value="" disabled>Select…</option>
                          {TIMELINES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="wt-challenge" className="field-label">
                          What are you trying to solve?
                        </label>
                        <textarea
                          id="wt-challenge"
                          name="challenge"
                          rows={4}
                          className="field-input"
                          placeholder="Where does work enter, where does it get stuck, and which systems does it cross today?"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="wt-systems" className="field-label">
                          Current systems <span className="font-normal text-[#6B7684]">(optional)</span>
                        </label>
                        <input
                          id="wt-systems"
                          name="systems"
                          type="text"
                          className="field-input"
                          placeholder="CRM, dialer, quoting tools, workforce tools…"
                        />
                        <p className="field-hint">
                          Helps us map the walkthrough to the stack you already run.
                        </p>
                      </div>
                    </div>
                    <div className="mt-7 flex flex-wrap items-center gap-5">
                      <button type="submit" className="btn-primary">
                        Send request <span aria-hidden="true">→</span>
                      </button>
                      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#6B7684]">
                        No newsletter. No drip sequence.
                      </p>
                    </div>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
